// 권한 레벨 정의
export type UserRole = 'owner' | 'admin' | 'editor' | 'viewer'

// 기능별 권한 정의 - 단순화된 권한
export interface Permission {
  ontology: {
    read: boolean       // 온톨로지 조회
    edit: boolean       // 온톨로지 편집 (클래스/관계/속성 생성/수정/삭제)
    export: boolean     // 스키마 내보내기/가져오기
  }
  data: {
    read: boolean       // 데이터 조회/다운로드
    upload: boolean     // 데이터 업로드/편집
    delete: boolean     // 데이터 삭제
  }
  sparql: {
    read: boolean       // 쿼리 조회
    write: boolean      // 쿼리 작성/편집/저장
    execute: boolean    // 쿼리 실행
  }
  analytics: {
    read: boolean       // 분석 결과 조회
    create: boolean     // 모델 생성/설정
    export: boolean     // 결과 내보내기
  }
  project: {
    read: boolean
    update: boolean
    delete: boolean
    share: boolean
    manageMembers: boolean
  }
}

// 역할별 권한 매트릭스 - 단순화된 권한
export const ROLE_PERMISSIONS: Record<UserRole, Permission> = {
  owner: {
    ontology: { read: true, edit: true, export: true },
    data: { read: true, upload: true, delete: true },
    sparql: { read: true, write: true, execute: true },
    analytics: { read: true, create: true, export: true },
    project: { read: true, update: true, delete: true, share: true, manageMembers: true }
  },
  admin: {
    ontology: { read: true, edit: true, export: true },
    data: { read: true, upload: true, delete: true },
    sparql: { read: true, write: true, execute: true },
    analytics: { read: true, create: true, export: true },
    project: { read: true, update: true, delete: false, share: true, manageMembers: true }
  },
  editor: {
    ontology: { read: true, edit: true, export: false },     // 편집 가능, 내보내기 불가
    data: { read: true, upload: true, delete: false },       // 업로드 가능, 삭제 불가
    sparql: { read: true, write: true, execute: true },      // 쿼리 작성/실행 가능
    analytics: { read: true, create: false, export: false }, // 조회만 가능
    project: { read: true, update: false, delete: false, share: false, manageMembers: false }
  },
  viewer: {
    ontology: { read: true, edit: false, export: true },     // 조회/내보내기만 가능
    data: { read: true, upload: false, delete: false },      // 조회만 가능
    sparql: { read: true, write: false, execute: true },     // 조회/실행만 가능
    analytics: { read: true, create: false, export: false }, // 조회만 가능
    project: { read: true, update: false, delete: false, share: false, manageMembers: false }
  }
}

// 권한 체크 함수들
export const hasPermission = (
  userRole: UserRole,
  module: keyof Permission,
  action: string
): boolean => {
  const permissions = ROLE_PERMISSIONS[userRole]
  const modulePermissions = permissions[module] as Record<string, boolean>
  return modulePermissions[action] || false
}

// 페이지 접근 권한 체크
export const canAccessPage = (userRole: UserRole, page: string): boolean => {
  switch (page) {
    case 'ontology':
    case 'schema':  // TBox (스키마캔버스) - SchemaDesigner page uses 'schema'
      return hasPermission(userRole, 'ontology', 'read')
    case 'data':
      return hasPermission(userRole, 'data', 'read')
    case 'sparql':
      return hasPermission(userRole, 'sparql', 'read')
    case 'analytics':
      return hasPermission(userRole, 'analytics', 'read')
    case 'projects':
      return hasPermission(userRole, 'project', 'read')
    default:
      return false
  }
}

// 프로젝트 권한 체크 헬퍼 함수들
export const canManageProject = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'project', 'update')

export const canDeleteProject = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'project', 'delete')

export const canShareProject = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'project', 'share')

export const canManageMembers = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'project', 'manageMembers')

// === 단순화된 권한 체크 함수들 ===
// 온톨로지 권한
export const canReadOntology = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'ontology', 'read')

export const canEditOntology = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'ontology', 'edit')

export const canExportOntology = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'ontology', 'export')

// 데이터 권한
export const canReadData = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'data', 'read')

export const canUploadData = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'data', 'upload')

export const canDeleteData = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'data', 'delete')

// SPARQL 권한
export const canReadSparql = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'sparql', 'read')

export const canWriteSparql = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'sparql', 'write')

export const canExecuteSparql = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'sparql', 'execute')

// AI 분석 권한
export const canReadAnalytics = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'analytics', 'read')

export const canCreateAnalytics = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'analytics', 'create')

export const canExportAnalytics = (userRole: UserRole): boolean => 
  hasPermission(userRole, 'analytics', 'export')

// 사용자가 프로젝트에 접근할 수 있는지 확인
interface ProjectAccessShape {
  owner?: string
  members?: Array<{ email: string }>
}

export const hasProjectAccess = (userEmail: string, project: ProjectAccessShape | null | undefined): boolean => {
  if (!project || !userEmail) return false

  // 프로젝트 소유자이거나 멤버인 경우 접근 가능
  if (project.owner === userEmail) return true

  // 멤버 목록에서 해당 사용자 찾기
  const member = project.members?.find((m) => m.email === userEmail)
  return !!member
}

// 권한 레벨 라벨 및 설명
export const ROLE_LABELS: Record<UserRole, string> = {
  owner: '소유자',
  admin: '관리자',
  editor: '편집자',
  viewer: '뷰어'
}

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  owner: '모든 권한 (프로젝트 삭제, 소유권 이전 포함)',
  admin: '관리 권한 (멤버 관리, 공유, 모든 기능 편집 가능)',
  editor: '온톨로지/데이터/SPARQL 편집, AI 분석 조회만 가능',
  viewer: '모든 내용 조회, 기존 SPARQL 쿼리 실행만 가능'
}